<template>
  <v-card>
    <v-stepper v-model="currentStep">
      <v-stepper-header>
        <v-stepper-item
          value="1"
          title="Setup Schedule"
          :complete="currentStep > 1"
        />
        <v-divider />
        <v-stepper-item
          value="2"
          title="Review Structure"
          :disabled="currentStep < 2"
        />
      </v-stepper-header>

      <v-stepper-window>
        <!-- Step 1: Setup Schedule -->
        <v-stepper-window-item value="1">
          <v-card-text>
            <v-expansion-panels v-model="expandedPanels" multiple>
              <!-- Panel 1: Generate Spreadsheet Template -->
              <v-expansion-panel value="step1">
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-file-excel</v-icon>
                  Step 1: Generate Spreadsheet Template
                  <v-chip size="x-small" variant="outlined" class="ml-2">Optional</v-chip>
                </v-expansion-panel-title>

                <v-expansion-panel-text class="pa-6">
                  <div class="text-subtitle-2 text-medium-emphasis mb-4">
                    Generate a blank spreadsheet template to define your schedule structure offline
                  </div>

                  <!-- Template Selection -->
                  <div class="mb-4">
                    <v-select
                      v-model="selectedTemplate"
                      :items="scheduleTemplates"
                      item-title="title"
                      item-value="id"
                      label="Schedule Template"
                      variant="outlined"
                      density="comfortable"
                    >
                      <template #item="{ props, item }">
                        <v-list-item v-bind="props">
                          <template #prepend>
                            <v-icon v-bind="getIconClass(item.raw.icon)" />
                          </template>
                        </v-list-item>
                      </template>
                    </v-select>
                  </div>

                  <!-- Structure Options -->
                  <div class="mb-4">
                    <div class="text-subtitle-1 mb-2">Structure:</div>
                    <v-radio-group v-model="templateConfig.structure" density="compact">
                      <v-radio value="manual">
                        <template #label>
                          <div>
                            <div class="font-weight-medium">Create Manually</div>
                            <div class="text-caption text-medium-emphasis">Define task columns yourself</div>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="auto" class="mt-2">
                        <template #label>
                          <div>
                            <div class="font-weight-medium">Use Drawing Sets, Drawings & Locations</div>
                            <div class="text-caption text-medium-emphasis">Automatically generate structure from project data</div>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                    <v-text-field
                      v-model.number="templateConfig.taskColumns"
                      type="number"
                      label="Task Columns"
                      density="compact"
                      class="mt-4"
                      @blur="clampTaskColumns"
                    />
                  </div>

                  <!-- WBS Options -->
                  <div class="mb-4">
                    <div class="text-subtitle-1 mb-2">WBS:</div>
                    <v-radio-group v-model="templateConfig.wbs" density="compact">
                      <v-radio value="manual">
                        <template #label>
                          <div>
                            <div class="font-weight-medium">Create Manually</div>
                            <div class="text-caption text-medium-emphasis">Define WBS in spreadsheet</div>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="auto" class="mt-2">
                        <template #label>
                          <div>
                            <div class="font-weight-medium">Generate Automatically</div>
                            <div class="text-caption text-medium-emphasis">Auto-generate WBS from structure</div>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </div>

                  <!-- Dates Options -->
                  <div v-if="usesDates" class="mb-4">
                    <div class="text-subtitle-1 mb-2">Dates:</div>
                    <v-checkbox
                      v-model="templateConfig.includeDates"
                      label="Include Date Columns"
                      density="compact"
                      hide-details
                    />
                    <v-radio-group
                      v-if="templateConfig.includeDates"
                      v-model="selectedTimeRangeTemplateId"
                      density="compact"
                      class="mt-4"
                    >
                      <template #label>
                        <div class="text-subtitle-2 mb-2">Time Range:</div>
                      </template>
                      <v-radio
                        v-for="template in templateTimeRangeTemplates"
                        :key="template.id"
                        :label="template.title"
                        :value="template.id"
                      />
                    </v-radio-group>
                  </div>

                  <!-- Include Options -->
                  <div class="mb-4">
                    <div class="text-subtitle-1 mb-2">Include:</div>
                    <v-checkbox
                      v-if="hasTaskSets"
                      v-model="templateConfig.includeTaskSets"
                      label="Task Sets"
                      density="compact"
                      hide-details
                    />
                    <v-checkbox
                      v-model="templateConfig.includeLocations"
                      label="Viewable Locations"
                      density="compact"
                      hide-details
                      :class="hasTaskSets ? 'mt-2' : ''"
                    />
                    <v-text-field
                      v-if="templateConfig.includeLocations"
                      v-model.number="templateConfig.viewableLocationsDepth"
                      type="number"
                      :min="1"
                      label="Viewable Location Columns"
                      density="compact"
                      class="mt-4"
                    />
                    <v-checkbox
                      v-if="templateConfig.includeDates && publishedCalendars.length > 1"
                      v-model="templateConfig.includeCalendarOverride"
                      label="Calendar Override Column"
                      density="compact"
                      hide-details
                      class="mt-2"
                    />
                  </div>

                  <div class="d-flex justify-end mt-4">
                    <v-btn
                      :disabled="!selectedTemplate"
                      color="success"
                      variant="flat"
                      size="large"
                      prepend-icon="mdi-download"
                      @click="generateSpreadsheet"
                    >
                      Generate Spreadsheet
                    </v-btn>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>

              <!-- Panel 2: Create Schedule & Import Tasks -->
              <v-expansion-panel value="step2">
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-calendar-plus</v-icon>
                  Step 2: Create Schedule & Import Tasks
                </v-expansion-panel-title>

                <v-expansion-panel-text class="pa-6">
                  <div class="text-subtitle-2 text-medium-emphasis mb-4">
                    Create a new schedule and import your completed spreadsheet
                  </div>

                  <v-row v-if="validationErrors.length">
                    <v-col cols="12">
                      <v-alert
                        type="error"
                        title="Validation Errors"
                      >
                        <ul class="mb-0">
                          <li v-for="error in validationErrors" :key="error">
                            {{ error }}
                          </li>
                        </ul>
                      </v-alert>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="newSchedule.title"
                        label="Schedule Title"
                        :error-messages="titleErrorMessage"
                        required
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Q1 2025 Construction"
                        @blur="titleBlurred = true"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-text-field
                        v-model="newSchedule.code"
                        label="Schedule Code"
                        :error-messages="codeErrorMessage"
                        required
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Q1-2025"
                        @blur="codeBlurred = true"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-combobox
                        v-model="newSchedule.tags"
                        v-model:search="tagSearch"
                        :items="[]"
                        label="Tags"
                        multiple
                        chips
                        closable-chips
                        variant="outlined"
                        density="comfortable"
                        hide-no-data
                        hide-selected
                        hint="Type a tag and press Tab or Enter to add"
                        persistent-hint
                        @keydown="handleTagKeydown"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-select
                        v-model="selectedTemplate"
                        :items="scheduleTemplates"
                        item-title="title"
                        item-value="id"
                        label="Template"
                        variant="outlined"
                        density="comfortable"
                      >
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template #prepend>
                              <v-icon v-bind="getIconClass(item.raw.icon)" />
                            </template>
                          </v-list-item>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12">
                      <v-file-input
                        v-model="selectedSpreadsheet"
                        label="Spreadsheet to Import"
                        variant="outlined"
                        density="comfortable"
                        accept=".xlsx,.xls,.xml"
                        prepend-icon="mdi-file-excel"
                        hint="Upload your completed spreadsheet (.xlsx) or a Primavera P6 file (.xml)"
                        persistent-hint
                        clearable
                        :rules="[v => !!v || 'Spreadsheet file is required']"
                      />
                    </v-col>

                    <!-- Calendar Selection: only once a file is chosen, so the
                         mapping can be pre-resolved from a P6 file's calendars -->
                    <v-col v-if="usesDates && hasFileSelected" cols="12">
                      <v-alert
                        v-if="publishedCalendars.length === 0"
                        type="error"
                        class="mb-4"
                      >
                        No published calendars available. A calendar is required for scheduling dates.
                      </v-alert>

                      <v-alert
                        v-else-if="publishedCalendars.length === 1"
                        type="info"
                        variant="tonal"
                        class="mb-4"
                      >
                        Calendar: <strong>{{ publishedCalendars[0].title }}</strong> will be applied to all time ranges
                      </v-alert>

                      <template v-else>
                        <div class="text-subtitle-2 mb-2">Calendar per Time Range</div>
                        <v-alert
                          v-if="autoMatchedCalendar"
                          type="success"
                          variant="tonal"
                          density="compact"
                          class="mb-2"
                        >
                          Matched calendar <strong>{{ autoMatchedCalendar.title }}</strong> from the P6 file by code ({{ autoMatchedCalendar.code }}). Adjust below if needed.
                        </v-alert>
                        <v-alert
                          v-else-if="isXmlFileSelected"
                          type="warning"
                          variant="tonal"
                          density="compact"
                          class="mb-2"
                        >
                          The P6 file's calendars don't match any published calendar yet. Select a calendar for each time range.
                        </v-alert>
                        <v-select
                          v-for="trt in timeRangeTemplates"
                          :key="trt.id"
                          v-model="calendarSelections[trt.id]"
                          :items="calendarDropdownItems"
                          item-title="label"
                          item-value="id"
                          :label="`Calendar for ${trt.title}`"
                          variant="outlined"
                          density="comfortable"
                          class="mb-2"
                        />
                      </template>
                    </v-col>
                  </v-row>

                  <div class="d-flex justify-end mt-4">
                    <v-btn
                      :disabled="!isSetupEnabled"
                      color="primary"
                      variant="flat"
                      size="large"
                      @click="handleContinue"
                    >
                      Continue
                    </v-btn>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-stepper-window-item>

        <!-- Step 2: Review Structure -->
        <review-structure-step
          v-if="currentStep === '2'"
          v-model:nested-tasks="nestedTasks"
          v-model:display-tasks="displayTasks"
          :p6-calendars="p6Calendars"
          v-model:changes="changes"
          :selected-schedule="selectedSchedule"
          :relationship-summary="relationshipSummary"
          :pruned-empty-wbs-count="prunedEmptyWbsCount"
          :duration-summary="durationSummary"
          :time-range-editability="timeRangeEditability"
          v-model:selected-items="selectedItems"
          v-model:open-items="openItems"
          step-value="2"
          @back="currentStep = '1'"
          @update:editable-time-range-ids="editableTimeRangeIds = $event"
          @import="startImport"
        />
      </v-stepper-window>
    </v-stepper>

    <calendar-import-dialog
      v-model="showCalendarDialog"
      :p6-calendars="p6Calendars"
      @confirmed="handleCalendarsConfirmed"
      @skipped="handleCalendarsSkipped"
    />

    <import-progress-dialog
      v-model="importingTasks"
      :completed="importCompleted"
      :error="importError"
      @done="handleImportComplete"
      @close="handleImportFailed"
    />

    <processing-progress-dialog
      :model-value="processing"
      :stage="progressStage"
      :done="progressDone"
      :total="progressTotal"
    />
  </v-card>
</template>

<script setup>
import { ref, computed, watch, shallowRef, nextTick } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useResourceStore } from '@/stores/resource';
import { useRouter } from 'vue-router';
import RandomIdGenerator from '@/utils/RandomIdGenerator';
import { ScheduleImporter } from '@/utils/ScheduleImporter';
import { SpreadsheetProcessor } from '@/utils/SpreadsheetProcessor';
import { runProcessingTask } from '@/workers/workerClient';
import { deepToRaw } from '@/utils/deepToRaw';
import ReviewStructureStep from './steps/ReviewStructureStep.vue';
import ImportProgressDialog from './dialogs/ImportProgressDialog.vue';
import ProcessingProgressDialog from './dialogs/ProcessingProgressDialog.vue';
import CalendarImportDialog from './dialogs/CalendarImportDialog.vue';
import { CalendarImporter } from '@/utils/CalendarImporter';
import { extractP6CalendarSummary, matchP6CalendarByCode, parseP6XmlDocument, P6_TASK_KEY } from '@/utils/XMLTaskParser';
import { yieldToPaint } from '@/utils/mainThreadScheduler';
import { applyTaskLocks } from '@/utils/TaskLockUtils';
import { extractApiErrorMessage } from '@/utils/ApiErrorUtils';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const scheduleStore = useScheduleStore();
const taskStore = useTaskStore();
const resourceStore = useResourceStore();
const router = useRouter();

const currentStep = ref('1');
const importingTasks = ref(false);
const importCompleted = ref(false);
const importError = ref(null);
// File-processing progress dialog state (handleContinue → processFile).
const processing = ref(false);
const progressStage = ref('');
const progressDone = ref(null);
const progressTotal = ref(null);
// shallowRef: these hold entire imported task trees (tens of thousands of
// objects on large P6 files). Deep reactivity would proxy every node for no
// benefit — they are only ever replaced wholesale, never mutated for the UI.
const nestedTasks = shallowRef([]);
const displayTasks = shallowRef([]);
const changes = shallowRef([]);
const selectedItems = ref([]);
const openItems = ref([]);
const taskSetsPayload = ref({});
const validationErrors = ref([]);

// P6 calendar import state (populated only for .xml uploads)
const p6Calendars = ref([]);
const taskCalendarLinks = ref(null);
const showCalendarDialog = ref(false);
// Lock keys the commit must present (P6 imports lock their tasks with 'p6')
const commitKeys = ref([]);
// What the P6 relationship import kept/dropped; shown on the review step
const relationshipSummary = ref(null);
// WBS nodes dropped for having no activities; shown on the review step
const prunedEmptyWbsCount = ref(0);
// Committed durations checked against P6's own; shown on the review step
const durationSummary = ref(null);

const generateNewId = () => {
  const idGenerator = new RandomIdGenerator();
  return idGenerator.generate();
};

const scheduleTemplates = computed(() => {
  const templates = scheduleStore.getScheduleTemplates;
  return [...templates].sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }));
});
const selectedTemplate = ref(null);
const selectedSpreadsheet = ref(null);
const selectedTimeRangeTemplateId = ref('');
const calendarSelections = ref({});
// Published calendar auto-matched from a P6 file's default calendar by
// deterministic code; null for Excel uploads or when no code matches.
const autoMatchedCalendar = ref(null);
const expandedPanels = ref([]);

const selectedFile = computed(() => {
  const value = selectedSpreadsheet.value;
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
});
const hasFileSelected = computed(() => selectedFile.value !== null);
const isXmlFileSelected = computed(() =>
  !!selectedFile.value && selectedFile.value.name.toLowerCase().endsWith('.xml')
);

const templateConfig = ref({
  structure: 'manual',
  wbs: 'manual',
  includeDates: false,
  includeTaskSets: true,
  includeLocations: true,
  includeCalendarOverride: false,
  viewableLocationsDepth: 1,
  taskColumns: 1
});

const newSchedule = ref({
  id: generateNewId(),
  title: '',
  code: '',
  tags: [],
  isDefault: false,
  current: true,
  scheduleTemplateId: '',
  createdByTeamId: '1',
  createdByUserId: '1',
  createdDate: Date.UTC,
  syncedDate: Date.UTC,
  timeRangeProfiles: []
});

const tagSearch = ref('');

const handleTagKeydown = (event) => {
  if (event.key !== 'Tab') return;
  const input = event.target;
  const value = (input?.value ?? tagSearch.value ?? '').trim();
  if (!value) return;
  event.preventDefault();
  if (!newSchedule.value.tags.includes(value)) {
    newSchedule.value.tags = [...newSchedule.value.tags, value];
  }
  tagSearch.value = '';
  if (input && 'value' in input) {
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

const selectedSchedule = computed(() => {
  if (currentStep.value === '2') {
    return {
      id: newSchedule.value.id,
      title: newSchedule.value.title,
      code: newSchedule.value.code,
      scheduleTemplateId: selectedTemplate.value,
      timeRangeCalendars: timeRangeCalendars.value
    };
  }
  return null;
});

const schedules = computed(() => scheduleStore.getSchedules || []);

const codeBlurred = ref(false);
const titleBlurred = ref(false);

const codeErrorMessage = computed(() => {
  if (!codeBlurred.value) {
    return '';
  }
  const code = newSchedule.value.code;
  if (code.length === 0) {
    return 'Code is required';
  }
  const codeLower = code.toLowerCase();
  const isDuplicate = schedules.value.some(schedule =>
    schedule.code?.toLowerCase() === codeLower && schedule.id !== newSchedule.value.id
  );
  if (isDuplicate) {
    return 'Code must be unique';
  }
  return '';
});

const titleErrorMessage = computed(() => {
  if (!titleBlurred.value) {
    return '';
  }
  const title = newSchedule.value.title;
  if (title.length === 0) {
    return 'Title is required';
  }
  const titleLower = title.toLowerCase();
  const isDuplicate = schedules.value.some(schedule =>
    schedule.title?.toLowerCase() === titleLower && schedule.id !== newSchedule.value.id
  );
  if (isDuplicate) {
    return 'Title must be unique';
  }
  return '';
});

const isTitleValid = computed(() => {
  const title = newSchedule.value.title;
  if (title.length <= 1) return false;
  const titleLower = title.toLowerCase();
  return !schedules.value.some(schedule =>
    schedule.title?.toLowerCase() === titleLower && schedule.id !== newSchedule.value.id
  );
});

const isSetupEnabled = computed(() => {
  const baseValid =
    selectedTemplate.value !== null &&
    isTitleValid.value &&
    newSchedule.value.code.length > 0 &&
    codeErrorMessage.value === '' &&
    selectedSpreadsheet.value !== null &&
    selectedSpreadsheet.value !== undefined &&
    (Array.isArray(selectedSpreadsheet.value) ? selectedSpreadsheet.value.length > 0 : true);

  if (!baseValid) return false;

  // If schedule uses dates, require calendar selections
  if (usesDates.value) {
    if (publishedCalendars.value.length === 0) return false;
    if (publishedCalendars.value.length > 1) {
      // All time range templates must have a calendar selected
      return timeRangeTemplates.value.every(trt => calendarSelections.value[trt.id]);
    }
  }

  return true;
});

const settings = computed(() => settingsStore.settings);

const commitTitle = computed({
  get: () => scheduleStore.getCommitTitle,
  set: (value) => scheduleStore.updateCommitTitle(value)
});

const commitMessage = computed({
  get: () => scheduleStore.getCommitMessage,
  set: (value) => scheduleStore.updateCommitMessage(value)
});

// Get available time range templates from selected schedule template (for import)
const timeRangeTemplates = computed(() => {
  if (selectedTemplate.value && scheduleStore.scheduleTemplatesObj[selectedTemplate.value]) {
    return scheduleStore.scheduleTemplatesObj[selectedTemplate.value][0].timeRangeTemplates || [];
  }
  return [];
});

// Which time ranges the created tasks may edit in RDrive, confirmed in the
// Save Tasks dialog (every task in a create flow is new). P6 imports default
// the first time range — the one carrying the P6 dates — to locked; the
// others stay editable.
const timeRangeEditability = computed(() => {
  const entries = timeRangeTemplates.value.map((template, index) => ({
    timeRangeTemplateId: template.id,
    title: template.title || template.id,
    state: isXmlFileSelected.value && index === 0 ? 'locked' : 'editable',
  }));
  return entries.length > 0 ? entries : null;
});
const editableTimeRangeIds = ref([]);

// Get available time range templates for template generation
const templateTimeRangeTemplates = computed(() => {
  if (selectedTemplate.value && scheduleStore.scheduleTemplatesObj[selectedTemplate.value]) {
    return scheduleStore.scheduleTemplatesObj[selectedTemplate.value][0].timeRangeTemplates || [];
  }
  return [];
});

const minTaskColumns = computed(() => {
  return templateConfig.value.structure === 'auto' ? 3 : 1;
});

// Check if selected template has timeranges
const usesDates = computed(() => {
  if (selectedTemplate.value) {
    const template = scheduleStore.scheduleTemplatesObj[selectedTemplate.value];
    if (template && template.length > 0 && template[0].timeRangeTemplates && template[0].timeRangeTemplates.length > 0) {
      const allNone = template[0].timeRangeTemplates.every(range => range.type === 'none');
      return !allNone;
    }
  }
  return false;
});

const publishedCalendars = computed(() => scheduleStore.getCalendars || []);

const calendarDropdownItems = computed(() =>
  publishedCalendars.value.map(c => ({ ...c, label: `${c.title} (${c.code})` }))
);

// On file selection, reset the calendar mapping; for a P6 XML, try to
// pre-resolve it by matching the file's default calendar code against the
// published calendars. When no match exists the selects stay empty and the
// user must map each time range manually (isSetupEnabled gates on it).
//
// The scan parses the whole file synchronously (DOMParser), which freezes the
// UI for seconds on large exports, so it runs behind the progress dialog:
// show it, yield a frame so it actually paints, then scan. The run id lets a
// newer file selection (or handleContinue) take over the dialog so a stale
// scan neither applies its result nor closes the dialog under a later run.
let scanRunId = 0;
// Parsed Document of the selected P6 file, kept from the pre-scan so
// handleContinue skips the file read and the blocking DOM parse. Plain
// variable, not a ref: a DOM tree must never be made reactive.
let prescannedXmlDoc = null;
watch(selectedFile, async (file) => {
  calendarSelections.value = {};
  autoMatchedCalendar.value = null;
  prescannedXmlDoc = null;
  if (!file || !file.name.toLowerCase().endsWith('.xml')) return;

  const runId = ++scanRunId;
  processing.value = true;
  progressStage.value = 'scanning-calendars';
  progressDone.value = null;
  progressTotal.value = null;
  try {
    await nextTick();
    await yieldToPaint();
    const xmlText = await file.text();
    if (runId !== scanRunId) return;
    prescannedXmlDoc = parseP6XmlDocument(xmlText);
    const { defaultCalendar } = extractP6CalendarSummary(prescannedXmlDoc);
    const matched = matchP6CalendarByCode(defaultCalendar, publishedCalendars.value);
    if (matched) {
      autoMatchedCalendar.value = matched;
      const selections = {};
      timeRangeTemplates.value.forEach(trt => { selections[trt.id] = matched.id; });
      calendarSelections.value = selections;
    }
  } catch (error) {
    console.error('Error pre-scanning P6 calendars:', error);
  } finally {
    if (runId === scanRunId) processing.value = false;
  }
});

// The schedule template (and therefore the time range templates) may be picked
// after the file; re-apply the auto-matched calendar to the current templates.
watch(() => timeRangeTemplates.value, (templates) => {
  if (!autoMatchedCalendar.value) return;
  const selections = {};
  templates.forEach(trt => { selections[trt.id] = autoMatchedCalendar.value.id; });
  calendarSelections.value = selections;
});

// Build timeRangeCalendars array from user selections
const timeRangeCalendars = computed(() => {
  if (!usesDates.value || publishedCalendars.value.length === 0) return [];

  // The API rejects entries with an empty calendarId or versionReference, so
  // templates without a resolved calendar are omitted rather than sent as null.
  return timeRangeTemplates.value
    .map(trt => {
      let calendarId;
      if (publishedCalendars.value.length === 1) {
        calendarId = publishedCalendars.value[0].id;
      } else {
        calendarId = calendarSelections.value[trt.id];
      }
      const calendar = publishedCalendars.value.find(c => c.id === calendarId);
      return {
        timeRangeTemplateId: trt.id,
        calendarId: calendar?.id || null,
        calendarVersionReference: calendar?.versionReference || null
      };
    })
    .filter(entry => entry.calendarId && entry.calendarVersionReference);
});

// Check if task sets exist in the project
const hasTaskSets = computed(() => {
  return taskStore.taskSetProfiles && taskStore.taskSetProfiles.length > 0;
});

// Watch structure changes and update taskColumns if needed
watch(() => templateConfig.value.structure, (newStructure) => {
  const minColumns = newStructure === 'auto' ? 3 : 1;
  if (templateConfig.value.taskColumns < minColumns) {
    templateConfig.value.taskColumns = minColumns;
  }
});

function clampTaskColumns() {
  if (!templateConfig.value.taskColumns || templateConfig.value.taskColumns < minTaskColumns.value) {
    templateConfig.value.taskColumns = minTaskColumns.value;
  }
}

// Watch for time range templates changes and set default
watch(templateTimeRangeTemplates, (newTemplates) => {
  if (newTemplates.length > 0) {
    const defaultTemplate = newTemplates.find(template => template.isDefault);
    selectedTimeRangeTemplateId.value = defaultTemplate ? defaultTemplate.id : newTemplates[0].id;
  } else {
    selectedTimeRangeTemplateId.value = '';
  }
}, { immediate: true });

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
};

const generateSpreadsheet = async () => {
  try {
    // Build update options based on template configuration
    const updateOptions = {
      defaultTaskCount: templateConfig.value.taskColumns,
      dates: templateConfig.value.includeDates,
      tasksets: templateConfig.value.includeTaskSets,
      viewableLocations: templateConfig.value.includeLocations,
      hideTaskIdColumn: true,
      hideWBSColumn: templateConfig.value.wbs === 'auto',
      maxViewableLocationsDepth: templateConfig.value.viewableLocationsDepth,
      taskSetProfiles: undefined,
      locationList: undefined,
      calendars: publishedCalendars.value,
      // Template rows have no time ranges, so override cells are pre-filled
      // from the configured per-time-range calendar mapping.
      timeRangeCalendars: timeRangeCalendars.value,
      includeCalendarOverride: templateConfig.value.includeCalendarOverride
    };

    if (updateOptions.tasksets) {
      updateOptions.taskSetProfiles = taskStore.taskSetProfiles;
    }

    if (updateOptions.viewableLocations) {
      updateOptions.locationList = resourceStore.locationsList;
    }

    // Generate tasks based on structure selection
    let tasks = [];
    if (templateConfig.value.structure === 'auto') {
      // Create nested structure from drawingSets and locations
      const drawingSets = resourceStore.drawingSets || [];
      const locations = resourceStore.locations || [];

      // Sort drawing sets alphabetically
      const sortedDrawingSets = [...drawingSets].sort((a, b) =>
        a.title.toLowerCase().localeCompare(b.title.toLowerCase())
      );

      sortedDrawingSets.forEach((drawingSet, dsIndex) => {
        // Create top-level task for drawing set
        const drawingSetTask = {
          id: '',
          title: drawingSet.title,
          wbs: [dsIndex + 1],
          lock: {
            structureEditable: true
          },
          tags: [],
          children: []
        };

        // Group locations by drawing within this drawing set
        const drawingGroups = {};
        locations
          .filter(loc => loc.drawingSetId === drawingSet.id)
          .forEach(loc => {
            const drawingTitle = loc.drawingTitle || 'Untitled Drawing';
            if (!drawingGroups[drawingTitle]) {
              drawingGroups[drawingTitle] = [];
            }
            drawingGroups[drawingTitle].push(loc);
          });

        // Sort drawing groups alphabetically and create second-level tasks
        const sortedDrawingEntries = Object.entries(drawingGroups).sort((a, b) =>
          a[0].toLowerCase().localeCompare(b[0].toLowerCase())
        );

        sortedDrawingEntries.forEach(([drawingTitle, drawingLocations], drawingIndex) => {
          const drawingTask = {
            id: '',
            title: drawingTitle,
            wbs: [dsIndex + 1, drawingIndex + 1],
            lock: {
              structureEditable: true
            },
            tags: [],
            children: []
          };

          // Sort locations alphabetically and create third-level tasks
          const sortedLocations = [...drawingLocations].sort((a, b) =>
            a.title.toLowerCase().localeCompare(b.title.toLowerCase())
          );

          sortedLocations.forEach((location, locIndex) => {
            const locationTask = {
              id: '',
              title: location.title,
              wbs: [dsIndex + 1, drawingIndex + 1, locIndex + 1],
              lock: {
                structureEditable: true
              },
              tags: [],
              children: []
            };

            // Set viewable location if enabled
            if (templateConfig.value.includeLocations && location.id) {
              locationTask.viewableLocationIds = [location.id];
            }

            drawingTask.children.push(locationTask);
          });

          drawingSetTask.children.push(drawingTask);
        });

        tasks.push(drawingSetTask);
      });
    }

    // Create a blank spreadsheet. Workbook generation is CPU-bound (exceljs
    // serialization) — run it in the processing worker; deepToRaw strips Vue
    // proxies so the payload can cross the worker boundary.
    const buffer = await runProcessingTask('exportXlsx', deepToRaw({
      tasks, // Pass generated tasks or empty array for manual mode
      selectedTimeRangeId: templateConfig.value.includeDates ? selectedTimeRangeTemplateId.value : null,
      updateOptions
    }));

    // Convert buffer to base64
    const base64 = arrayBufferToBase64(buffer);

    // Get selected template info for filename
    const template = scheduleTemplates.value.find(t => t.id === selectedTemplate.value);
    const filename = `${template?.title || 'Schedule'}_Template.xlsx`;

    // Prepare data for server request
    const requestData = {
      excelBuffer: base64,
      filename: filename
    };

    // Step 1: Generate token
    const tokenResponse = await fetch('https://paginated-report-service.rdrive.io/api/excel/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    if (!tokenResponse.ok) {
      throw new Error(`Server responded with ${tokenResponse.status}: ${tokenResponse.statusText}`);
    }

    // Get the token from response
    const tokenData = await tokenResponse.json();

    if (!tokenData.token) {
      throw new Error('No token received from server');
    }

    // Step 2: Use token to download the file
    const downloadUrl = `https://paginated-report-service.rdrive.io/api/excel/download?token=${tokenData.token}`;

    // Use iframe for download
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);

    // Remove iframe after download has started
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 2000);

  } catch (error) {
    console.error('Error generating spreadsheet:', error);
    alert('Error generating spreadsheet. Please try again.');
  }
};

const handleContinue = async () => {
  codeBlurred.value = true;
  titleBlurred.value = true;
  if (!isSetupEnabled.value) return;

  // Take the dialog over from any still-running calendar pre-scan.
  scanRunId++;
  processing.value = true;
  progressStage.value = 'reading-file';
  progressDone.value = null;
  progressTotal.value = null;

  try {
    const file = Array.isArray(selectedSpreadsheet.value)
      ? selectedSpreadsheet.value[0]
      : selectedSpreadsheet.value;

    // Get the selected time range template object
    const selectedTimeRangeTemplate = timeRangeTemplates.value.length > 0
      ? timeRangeTemplates.value.find(t => t.isDefault) || timeRangeTemplates.value[0]
      : null;

    // Create a temporary schedule object for processing
    const tempSchedule = {
      id: newSchedule.value.id,
      title: newSchedule.value.title,
      code: newSchedule.value.code,
      scheduleTemplateId: selectedTemplate.value,
      timeRangeCalendars: timeRangeCalendars.value
    };

    // Process the spreadsheet file
    const processor = new SpreadsheetProcessor(tempSchedule, selectedTimeRangeTemplate);
    const result = await processor.processFile(file, {
      xmlDoc: prescannedXmlDoc,
      onProgress: ({ stage, done, total }) => {
        progressStage.value = stage;
        progressDone.value = done ?? null;
        progressTotal.value = total ?? null;
      }
    });

    if (result.isValid) {
      nestedTasks.value = result.nestedTasks;
      displayTasks.value = result.displayTasks;
      changes.value = result.changes;
      taskSetsPayload.value = result.taskSetsPayload;
      p6Calendars.value = result.p6Calendars || [];
      taskCalendarLinks.value = result.taskCalendarLinks || null;
      commitKeys.value = result.commitKeys || [];
      relationshipSummary.value = result.relationshipSummary || null;
      prunedEmptyWbsCount.value = result.prunedEmptyWbsCount || 0;
      durationSummary.value = result.durationSummary || null;
      validationErrors.value = [];
      currentStep.value = '2';
    } else {
      validationErrors.value = result.validationErrors || result.errors || [];
      console.error('Spreadsheet validation failed:', validationErrors.value);
    }
  } catch (error) {
    console.error('Error processing spreadsheet:', error);
    alert('Error processing spreadsheet. Please check the file and try again.');
  } finally {
    processing.value = false;
  }
};

// Entry point from the review step's "import" action. For P6 XML uploads that
// carry calendars, confirm/import those first (re-linking tasks) before creating
// the schedule and committing.
const startImport = async () => {
  if (p6Calendars.value.length) {
    showCalendarDialog.value = true;
    return;
  }
  await createSchedule();
};

const handleCalendarsConfirmed = async ({ calendarsByP6Id }) => {
  try {
    const calendarImporter = new CalendarImporter(appStore.apiClient, settings.value.projectCode);
    calendarImporter.relinkTasks(nestedTasks.value, taskCalendarLinks.value, calendarsByP6Id);
  } catch (error) {
    console.error('Error re-linking tasks to imported calendars:', error);
  }
  await createSchedule();
};

const handleCalendarsSkipped = async () => {
  await createSchedule();
};

const createSchedule = async () => {
  importingTasks.value = true;
  importCompleted.value = false;
  importError.value = null;

  try {
    newSchedule.value.scheduleTemplateId = selectedTemplate.value;
    newSchedule.value.timeRangeCalendars = timeRangeCalendars.value;

    // Every task is new here: stamp the confirmed editable time ranges (a
    // commit without editableTimeRangeTemplateIds would lock every range).
    applyTaskLocks(nestedTasks.value, new Map(), editableTimeRangeIds.value);

    await appStore.apiClient.post(
      `/api/${settings.value.projectCode}/schedules`,
      newSchedule.value,
      async (createdSchedule) => {
        try {
          // Add the created schedule to the store
          scheduleStore.addSchedule(createdSchedule);

          // Import the tasks
          const importer = new ScheduleImporter(appStore.apiClient, settings.value);
          await importer.importTasks({
            scheduleId: createdSchedule.id,
            tasks: nestedTasks.value,
            commitTitle: commitTitle.value || 'Initial import',
            commitMessage: commitMessage.value || `Created schedule from spreadsheet`,
            taskSetsPayload: taskSetsPayload.value,
            keys: commitKeys.value,
            // P6 ends are the source FinishDates — commit them verbatim.
            preserveEndDates: commitKeys.value.includes(P6_TASK_KEY)
          });

          importCompleted.value = true;
          commitTitle.value = '';
          commitMessage.value = '';
        } catch (error) {
          console.error('Error importing tasks:', error);
          importError.value = extractApiErrorMessage(error);
        }
      }
    );
  } catch (error) {
    console.error('Error creating schedule:', error);
    importError.value = extractApiErrorMessage(error);
  }
};

// Failed commit acknowledged: close the dialog but keep the review step's
// state so the user can fix the problem and retry the import.
const handleImportFailed = () => {
  importingTasks.value = false;
  importError.value = null;
};

const handleImportComplete = () => {
  importingTasks.value = false;
  currentStep.value = '1';
  nestedTasks.value = [];
  displayTasks.value = [];
  changes.value = [];
  taskSetsPayload.value = {};
  newSchedule.value.title = '';
  newSchedule.value.code = '';
  newSchedule.value.tags = [];
  newSchedule.value.id = generateNewId();
  tagSearch.value = '';
  titleBlurred.value = false;
  codeBlurred.value = false;
  selectedSpreadsheet.value = null;
  selectedTemplate.value = null;
  calendarSelections.value = {};
  autoMatchedCalendar.value = null;
  p6Calendars.value = [];
  taskCalendarLinks.value = null;
  showCalendarDialog.value = false;
  commitKeys.value = [];
  relationshipSummary.value = null;
  prunedEmptyWbsCount.value = 0;
  durationSummary.value = null;
  router.push({ name: 'Import' });
};

const memoize = (fn) => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

const getIconClass = memoize((icon) => {
  if (icon.startsWith('mdi-')) {
    return { icon: icon };
  } else if (icon.startsWith('fa-') || icon.startsWith('fas fa-') || icon.startsWith('fab fa-') || icon.startsWith('far fa-')) {
    return { icon: 'fa:' + icon };
  } else {
    return { icon: `md:${icon}` };
  }
});

</script>

<style scoped>
/* Add your styles here */
</style>
